import { useEffect, useState } from 'react'
import { type EPLITrainerDevelopmentPlanAttendee, type EPLITrainerDevelopmentPlan } from '../../../models/EPLITrainerDevelopmentPlan'
import { toast } from 'react-toastify'
import { BlueButton } from '../../customComponents/buttons/BlueButton'
import { AddCircle, CalendarMonth } from '@mui/icons-material'
import { Box, CircularProgress, Typography } from '@mui/material'
import { EPLITrainerDevelopmentPlanModal } from './EPLITrainerDevelopmentPlanModal'
import { BannerModal } from '../../customComponents/BannerModal'
import { DeleteEPLITrainerDevelopmentPlan, GetEPLITrainerDevelopmentPlans, UpdateEPLITrainerDevelopmentPlan } from '../../../services/EPLITrainerDevelopmentPlanService'
import { type ThingsToConsiderSection } from './priorityActivities/ThingsToConsiderCard'
import { BasicTable, type BasicTableColumn } from './priorityActivities/BasicTable'
import { ThingsToConsiderButton } from './priorityActivities/ThingsToConsiderButton'
import { EPLITrainerDevelopmentPlanTrackingModal } from './EPLITrainerDevelopmentPlanTrackingModal'
import { SubBasicTable, type SubBasicTableColumn, type SubBasicTableRow } from './priorityActivities/SubBasicTable'
import { PriorityOneActivityName } from '../../../models/PriorityOneActivityName'
import { useModalState } from '../../../hooks/use-modalState'
import { DeleteItemModal } from './DeleteItemModal'
import type { AttendeeRequest, UpdateEPLITrainerDevelopmentPlanRequest } from '../../../models/requestObjects/priorityOne/epliTrainerDevelopmentPlanRequestObjects'
import { handleApiResponse } from '../../../utils/apiHelpers'

interface Props {
  priorityId: number
  annualPlanId: number
  orgId: number
  readOnly: boolean
  trackingReadonly: boolean
  implementationPhase?: boolean
  showTableOnly?: boolean
  hideExpand?: boolean
  isTracking?: boolean
  planOrgId: number
  budgetOverviewView?: boolean
}

interface EPLITableRow extends EPLITrainerDevelopmentPlan {
  disableTracking: boolean
  trainingTitle: string
  budgetRequestString: string
  actualBudgetAmountString: string
  expandedContent: JSX.Element | null
}

export const EPLITrainerDevelopmentPlanList = (props: Props): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [epliTrainerDevelopmentPlans, setEPLITrainerDevelopmentPlans] = useState<EPLITrainerDevelopmentPlan[]>([])
  const [rows, setRows] = useState<EPLITableRow[]>([])
  const [attendeeWithAdditionalDetailsToDelete, setAttendeeWithAdditionalDetailsToDelete] = useState<EPLITrainerDevelopmentPlanAttendee | null>(null)
  const [attendeeWithTrackingToDelete, setAttendeeWithTrackingToDelete] = useState<EPLITrainerDevelopmentPlanAttendee | null>(null)
  const { modalState, startModal, currentItem, handleCloseModal } = useModalState<EPLITrainerDevelopmentPlan, 'editAdditionalDetails'>(PriorityOneActivityName.EpliPlan)

  const attendeeToRequest = (attendee: EPLITrainerDevelopmentPlanAttendee): AttendeeRequest => ({
    id: attendee.id,
    fullName: attendee.fullName,
    isdId: attendee.isd.id,
    isAttendeeFromDistrict: attendee.district != null,
    districtId: attendee.district?.id,
    status: attendee.status,
    completionStatus: attendee.completionStatus ?? null
  })

  const loadEPLITrainerDevelopmentPlans = async (): Promise<void> => {
    setIsLoading(true)
    const plans = await handleApiResponse(GetEPLITrainerDevelopmentPlans(props.priorityId, props.implementationPhase !== true))
    if (plans != null) setEPLITrainerDevelopmentPlans(plans)
    setIsLoading(false)
  }

  const onSuccess = async (): Promise<void> => {
    setIsLoading(true)
    await loadEPLITrainerDevelopmentPlans()
    await handleCloseModal()
  }

  useEffect(() => {
    void loadEPLITrainerDevelopmentPlans()
  }, [])

  useEffect(() => {
    const tRows = epliTrainerDevelopmentPlans.sort(sortPlans).map(r => ({
      ...r,
      disableTracking: r.attendees?.length === 0,
      trainingTitle: r.epliTraining.title,
      budgetRequestString: `$${r.isUpdate ? (r.adjustedDollarAmount ?? 0).toLocaleString() : (r.budgetRequest ?? 0).toLocaleString()}`,
      actualBudgetAmountString: (r.actualBudgetAmount != null && !isNaN(r.actualBudgetAmount)) ? `$${r.actualBudgetAmount.toLocaleString()}` : '-',
      expandedContent: expandedEPLIContent(r),
      isUpdated: r.isUpdate && r.attendees?.some(a => a.fullName != null),
      isTracked: r.attendees?.some(a => a.completionStatus != null) ?? false
    }))
    setRows(tRows)
  }, [epliTrainerDevelopmentPlans])

  const handleDeleteAttendeeAdditionalDetails = async (attendee: EPLITrainerDevelopmentPlanAttendee): Promise<void> => {
    const epliTrainerDevelopmentPlan = epliTrainerDevelopmentPlans.find(e => e.attendees.some(a => a.id === attendee.id))
    if (epliTrainerDevelopmentPlan == null) {
      toast.error('Could not find the associated EPLI Trainer Plan for this attendee.')
      return
    }

    epliTrainerDevelopmentPlan.attendees = epliTrainerDevelopmentPlan.attendees.filter(a => a.id !== attendee.id)
    const updatedEpliTrainerDevelopmentPlan: UpdateEPLITrainerDevelopmentPlanRequest = {
      id: epliTrainerDevelopmentPlan.id,
      priorityId: epliTrainerDevelopmentPlan.priorityId,
      epliTrainingId: epliTrainerDevelopmentPlan.epliTraining.id,
      numberOfAttendees: epliTrainerDevelopmentPlan.numberOfAttendees,
      budgetRequest: epliTrainerDevelopmentPlan.budgetRequest,
      actualAmount: null,
      attendees: epliTrainerDevelopmentPlan.attendees.map(attendeeToRequest),
      isUpdate: epliTrainerDevelopmentPlan.isUpdate,
      isImplementationPhase: props.implementationPhase ?? false
    }

    const { success, errors } = await UpdateEPLITrainerDevelopmentPlan(updatedEpliTrainerDevelopmentPlan)
    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }
    await loadEPLITrainerDevelopmentPlans()

    toast.success('EPLI Trainer Plan Updated')
    setAttendeeWithAdditionalDetailsToDelete(null)
  }

  const handleDeleteAttendeeTracking = async (attendee: EPLITrainerDevelopmentPlanAttendee): Promise<void> => {
    const epliTrainerDevelopmentPlan = epliTrainerDevelopmentPlans.find(e => e.attendees.some(a => a.id === attendee.id))
    if (epliTrainerDevelopmentPlan == null) {
      toast.error('Could not find the associated EPLI Trainer Plan for this attendee.')
      return
    }
    epliTrainerDevelopmentPlan.attendees = epliTrainerDevelopmentPlan.attendees.map(a => {
      if (a.id === attendee.id) {
        a.completionStatus = null
      }
      return a
    })
    const updatedEpliTrainerDevelopmentPlan: UpdateEPLITrainerDevelopmentPlanRequest = {
      id: epliTrainerDevelopmentPlan.id,
      priorityId: epliTrainerDevelopmentPlan.priorityId,
      epliTrainingId: epliTrainerDevelopmentPlan.epliTraining.id,
      numberOfAttendees: epliTrainerDevelopmentPlan.numberOfAttendees,
      budgetRequest: epliTrainerDevelopmentPlan.budgetRequest,
      actualAmount: null,
      attendees: epliTrainerDevelopmentPlan.attendees.map(attendeeToRequest),
      isUpdate: epliTrainerDevelopmentPlan.isUpdate,
      isImplementationPhase: props.implementationPhase ?? false
    }
    const { success, errors } = await UpdateEPLITrainerDevelopmentPlan(updatedEpliTrainerDevelopmentPlan)
    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }
    await loadEPLITrainerDevelopmentPlans()

    toast.success('EPLI Trainer Plan Updated')
    setAttendeeWithTrackingToDelete(null)
  }

  const thingsToConsiderSections: ThingsToConsiderSection[] = [
    {
      title: '',
      items: [
        'Review the current list of EPLI Trainers to determine the EPLI Trainer needs for the upcoming year.',
        'Seek administrator approval to recruit new EPLI Trainers.',
        'Recruit qualified and motivated school staff to become EPLI Trainers based on needs and share information about the EPLI application and training dates.'
      ]
    }
  ]

  const isEmpty = epliTrainerDevelopmentPlans.length === 0
  const columns: BasicTableColumn[] = [
    { id: 'trainingTitle', label: 'Training Title', minWidth: 200 },
    { id: 'numberOfAttendees', label: 'Attendance Count', minWidth: 200 },
    { id: 'budgetRequestString', label: 'Budget', minWidth: 100 },
    { id: 'actualBudgetAmountString', label: 'Budget Used', minWidth: 100 }
  ]

  const expandedEPLIContent = (epliTrainerDevelopmentPlan: EPLITrainerDevelopmentPlan): JSX.Element | null => {
    const columns: SubBasicTableColumn[] = [
      { id: 'fullName', label: 'Attendee Name', minWidth: 100 },
      { id: 'isd', label: 'ISD' },
      { id: 'district', label: 'District' },
      { id: 'status', label: 'Type' },
      { id: 'action', label: '' },
      { id: 'completionStatus', label: 'Completion Status', isTrackingColumn: true },
      { id: 'trackingAction', label: '', isTrackingColumn: true }
    ]

    const subRows: SubBasicTableRow[] = [
      ...epliTrainerDevelopmentPlan.attendees?.map(attendee => {
        return {
          ...attendee,
          id: attendee.id,
          fullName: attendee.fullName,
          isd: attendee.isd.name,
          district: attendee.district?.name,
          status: attendee.status,
          action: '',
          completionStatus: attendee.completionStatus ?? '-',
          trackingAction: ''
        }
      }) ?? []
    ]

    const hasAttendeeWithAdditionalDetails = epliTrainerDevelopmentPlan.attendees?.some(a => a.id != null)
    const hasTrackingDetails = epliTrainerDevelopmentPlan.attendees?.some(a => a.completionStatus != null)
    const handleEditAdditionalDetails = (): void => {
      void startModal('editAdditionalDetails', epliTrainerDevelopmentPlan)
    }
    const handleEditTracking = (): void => {
      void startModal('track', epliTrainerDevelopmentPlan)
    }
    if (!hasAttendeeWithAdditionalDetails) return null
    return (
      <Box
        sx={{
          p: '2rem',
          backgroundColor: 'var(--grey-100)'
        }}
      >
        <SubBasicTable
          dataTestId={`epliTrainerDevelopmentPlanAttendeesTable-${epliTrainerDevelopmentPlan.id}`}
          columns={columns}
          rows={subRows}
          readonly={props.readOnly}
          trackingReadonly={props.trackingReadonly}
          hasTrackingDetails={hasTrackingDetails}
          onEditAdditionalDetails={handleEditAdditionalDetails}
          onDeleteAdditionalDetails={(attendee) => { setAttendeeWithAdditionalDetailsToDelete(attendee) }}
          onEditTracking={handleEditTracking}
          onDeleteTracking={(attendee) => { setAttendeeWithTrackingToDelete(attendee) }}
        />
      </Box>
    )
  }

  const sortPlans = (a: EPLITrainerDevelopmentPlan, b: EPLITrainerDevelopmentPlan): number => {
    const aHasCompletionStatus = a.attendees?.some(attendee =>
      attendee.completionStatus != null &&
      attendee.completionStatus.trim() !== ''
    ) ?? false

    const bHasCompletionStatus = b.attendees?.some(attendee =>
      attendee.completionStatus != null &&
      attendee.completionStatus.trim() !== ''
    ) ?? false

    if (aHasCompletionStatus && !bHasCompletionStatus) return -1
    if (!aHasCompletionStatus && bHasCompletionStatus) return 1

    return a.epliTraining.title.localeCompare(b.epliTraining.title)
  }

  return <>
    {props.showTableOnly !== true && (
      <Box sx={{ width: '100%', alignItems: 'center' }}>
        {!props.readOnly &&
          <BlueButton onClick={() => { void startModal('add') }} dataTestId='addEPLITrainerDevelopmentPlanButton'>
            Add EPLI Trainer Plan <AddCircle sx={{ ml: '10px' }} fontSize='small' />
          </BlueButton>
      }

        <Box sx={{ float: 'right' }}>
          <ThingsToConsiderButton
            buttonDataTestId='epliTrainerDevelopmentPlanThingsToConsiderButton'
            thingsToConsiderSections={thingsToConsiderSections}
            cardDataTestId='epliTrainerDevelopmentPlanThingsToConsiderCard'
          />
        </Box>
      </Box>
    )}

    <Box
      data-testid='epliTrainerDevelopmentPlanList'
      sx={{
        border: '1px solid var(--blue-300)',
        backgroundColor: 'var(--white)',
        borderRadius: '8px',
        padding: isEmpty ? '24px' : '0',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px 10px 10px -8px #3B353E4D'
      }}
    >
      {isLoading
        ? <CircularProgress />
        : (isEmpty
            ? <>
              <CalendarMonth sx={{ color: 'var(--blue-300)', width: '144px', height: '144px', alignSelf: 'center' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                <Typography variant='h4'>No EPLI Trainer Plan Added</Typography>
                {!props.readOnly &&
                  <Typography variant='body1' fontSize='16px'>
                    Use the Add EPLI Trainer Plan button to add an EPLI Trainer Plan to your Annual Plan.
                  </Typography>
                }
              </Box>
            </>
            : <BasicTable
                dataTestId='epliTrainerDevelopmentPlanTable'
                columns={columns}
                rows={rows}
                isLoading={isLoading}
                readonly={props.readOnly}
                trackingReadonly={props.trackingReadonly}
                onEdit={async (item) => { await startModal('update', item) }}
                onDelete={async (item) => { await startModal('delete', item) }}
                hideExpand={props.hideExpand}
                implementationPhase={props.implementationPhase}
                onUpdate={async (item) => { await startModal('update', item) }}
                hideDelete={props.isTracking === true}
                onTrack={async (item) => { await startModal('track', item) }}
                budgetOverviewView={props.budgetOverviewView}
            />
          )
      }

      {(modalState === 'add' || ((modalState === 'update' || modalState === 'editAdditionalDetails') && currentItem != null)) &&
        <EPLITrainerDevelopmentPlanModal
          usedIds={epliTrainerDevelopmentPlans.map(r => r.epliTraining.id ?? -1)}
          annualPlanId={props.annualPlanId}
          orgId={props.orgId}
          priorityId={props.priorityId}
          onClose={handleCloseModal}
          onSave={onSuccess}
          epliTrainerDevelopmentPlan={currentItem}
          implementationPhase={props.implementationPhase}
          planOrgId={props.planOrgId}
          startOnAdditionalDetails={modalState === 'editAdditionalDetails'}
        />
      }

      {modalState === 'track' && currentItem != null && (
        <EPLITrainerDevelopmentPlanTrackingModal
          onClose={handleCloseModal}
          onSave={onSuccess}
          epliTrainerDevelopmentPlan={currentItem}
          implementationPhase={props.implementationPhase === true}
        />
      )}

      {modalState === 'delete' && currentItem != null &&
        <DeleteItemModal
          item={currentItem}
          deleteFunction={DeleteEPLITrainerDevelopmentPlan}
          onClose={handleCloseModal}
          onConfirm={onSuccess}
          itemType='EPLI Trainer Plan'
        />
      }

      {attendeeWithAdditionalDetailsToDelete != null && (
        <BannerModal
          title='Are you sure?'
          cancelButtonText='No, Cancel'
          confirmButtonText='Yes, Delete'
          dataTestId='confirmDeleteEPLITrainerDevelopmentPlanAttendeeAdditionalDetailsModal'
          bodyContent={'Are you sure you want to delete this item? This will also remove any tracking data associated with it.'}
          onClose={() => { setAttendeeWithAdditionalDetailsToDelete(null) }}
          onConfirm={() => { void handleDeleteAttendeeAdditionalDetails(attendeeWithAdditionalDetailsToDelete) }}
          maxWidth='xs'
          bottom=''
          position='fixed'
        />
      )}

      {attendeeWithTrackingToDelete != null && (
        <BannerModal
          title='Are you sure?'
          cancelButtonText='No, Cancel'
          confirmButtonText='Yes, Delete'
          dataTestId='confirmDeleteEPLITrainerDevelopmentPlanAttendeeTrackingModal'
          bodyContent={'Are you sure you want to delete this item?'}
          onClose={() => { setAttendeeWithTrackingToDelete(null) }}
          onConfirm={() => { void handleDeleteAttendeeTracking(attendeeWithTrackingToDelete) }}
          maxWidth='xs'
          bottom=''
          position='fixed'
        />
      )}
    </Box>
  </>
}
